import type { JSONContent } from "@tiptap/core";
import type { PostgrestError } from "@supabase/supabase-js";
import { isSupabaseConfigured } from "@/lib/env";
import { supabase } from "@/lib/supabase";
import {
  mapProjectArtstyleReference,
  type ProjectArtDirectionUpdateInput,
  type ProjectImageSettingsView,
} from "@/features/image-generation";
import {
  buildProjectDashboardSummary,
  buildUpgradePrompt,
  hasProjectArtDirection,
  mapCapacityRow,
  mapProjectRecordToCard,
  PROJECT_LIMITS,
  sortProjectsByLastModified,
} from "@/features/projects/mappers/projectMappers";
import {
  createProjectArtstyleReferenceSignedUrl,
  createProjectCoverSignedUrl,
  deleteProjectArtstyleReference,
  deleteProjectCover,
  PROJECT_ARTSTYLE_REFERENCE_LIMIT,
  uploadProjectArtstyleReference,
  uploadProjectCover,
} from "@/features/projects/api/projectStorage";
import {
  projectArtDirectionSchema,
  projectSchema,
  updateProjectSchema,
} from "@/features/projects/validation/projectSchemas";
import {
  readStoryImportReviewBundle,
  readStoryImportSummary,
} from "@/features/story-import/api/storyImportClient";
import {
  buildProjectStoryImportReviewRoute,
  buildProjectStoryImportRoute,
  mapStoryImportBundleToProjectImportReviewPanel,
} from "@/features/story-import/storyImportMappers";
import { getEntityCardHref } from "@/types/entity";
import type {
  ProjectActiveImportJobView,
  CreateProjectInput,
  ProjectAccessSelectionState,
  ProjectCapacitySummary,
  ProjectCardView,
  ProjectDashboardSummary,
  ProjectDashboardActivityAction,
  ProjectDashboardActivityItem,
  ProjectImportReviewLockView,
  ProjectImportReviewPanelView,
  ProjectRecord,
  ProjectStatus,
  SubscriptionTier,
  UpdateProjectInput,
  UpgradePromptState,
} from "@/types/projects";
import type { Database } from "@/lib/database.types";

type DashboardStoryRow = Pick<
  Database["public"]["Tables"]["stories"]["Row"],
  "body" | "created_at" | "id" | "image_paths" | "mode" | "project_id" | "title" | "type" | "updated_at"
>;

type DashboardChapterRow = Pick<
  Database["public"]["Tables"]["chapters"]["Row"],
  "body" | "created_at" | "id" | "image_path" | "story_id" | "title" | "updated_at"
>;

type DashboardLoreEntryRow = Pick<
  Database["public"]["Tables"]["lore_entries"]["Row"],
  "cover_image_path" | "created_at" | "id" | "project_id" | "title" | "updated_at"
>;

type DashboardEntityRow = Pick<
  Database["public"]["Tables"]["entity_cards"]["Row"],
  "created_at" | "id" | "image_path" | "name" | "project_id" | "updated_at"
>;

type DashboardTimelineRow = Pick<
  Database["public"]["Tables"]["timeline_events"]["Row"],
  "created_at" | "id" | "image_path" | "project_id" | "title" | "updated_at"
>;

export type ProjectActionErrorCode =
  | "generic"
  | "missing-config"
  | "project-limit-reached"
  | "access-denied"
  | "not-found";

interface ProjectActionErrorOptions {
  cause?: unknown;
  upgradePrompt?: UpgradePromptState;
}

export class ProjectActionError extends Error {
  readonly cause?: unknown;
  readonly upgradePrompt?: UpgradePromptState;

  constructor(
    public readonly code: ProjectActionErrorCode,
    message?: string,
    options?: ProjectActionErrorOptions,
  ) {
    super(message ?? code);
    this.name = "ProjectActionError";
    this.cause = options?.cause;
    this.upgradePrompt = options?.upgradePrompt;
  }
}

function ensureConfigured() {
  if (!isSupabaseConfigured()) {
    throw new ProjectActionError(
      "missing-config",
      "Supabase environment variables are not configured.",
    );
  }
}

async function getCurrentUserId() {
  ensureConfigured();

  const {
    data: { user },
  } = await supabase.auth.getUser();

  if (!user) {
    throw new ProjectActionError("access-denied", "No authenticated user available.");
  }

  return user.id;
}

function parseProjectLimitError(message: string) {
  const [prefix, tier, currentCount, allowedCount, recommendedTier] = message.split("|");

  if (
    prefix !== "PROJECT_LIMIT_REACHED"
    || !tier
    || !currentCount
    || !allowedCount
    || !recommendedTier
  ) {
    return null;
  }

  return buildUpgradePrompt(
    tier as SubscriptionTier,
    Number(currentCount),
    Number(allowedCount),
    recommendedTier as SubscriptionTier,
  );
}

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" ? value as Record<string, unknown> : {};
}

function asStringArray(value: unknown) {
  return Array.isArray(value)
    ? value.filter((item): item is string => typeof item === "string" && item.trim().length > 0)
    : [];
}

function mapProjectAccessSelectionState(rawValue: unknown): ProjectAccessSelectionState {
  const value = asRecord(rawValue);
  const candidateProjects = Array.isArray(value.candidate_projects)
    ? value.candidate_projects
      .map((item) => {
        const candidate = asRecord(item);
        const projectId = typeof candidate.id === "string" ? candidate.id : "";
        if (!projectId) {
          return null;
        }

        return {
          genre: typeof candidate.genre === "string" ? candidate.genre : "",
          projectId,
          status: candidate.status === "archived" ? "archived" : "active",
          title: typeof candidate.title === "string" ? candidate.title : "",
          updatedAt:
            typeof candidate.updated_at === "string"
              ? candidate.updated_at
              : new Date(0).toISOString(),
        };
      })
      .filter((item): item is ProjectAccessSelectionState["candidateProjects"][number] => Boolean(item))
    : [];

  return {
    candidateProjects,
    projectsLimit:
      typeof value.projects_limit === "number" && Number.isFinite(value.projects_limit)
        ? value.projects_limit
        : PROJECT_LIMITS.free,
    projectsUsed:
      typeof value.projects_used === "number" && Number.isFinite(value.projects_used)
        ? value.projects_used
        : 0,
    required: value.required === true,
    requiredProjectCount:
      typeof value.required_project_count === "number"
      && Number.isFinite(value.required_project_count)
        ? value.required_project_count
        : PROJECT_LIMITS.free,
    selectedProjectIds: asStringArray(value.selected_project_ids),
    tier:
      value.tier === "crafter" || value.tier === "author" || value.tier === "free"
        ? value.tier
        : "free",
  };
}

function mapProjectError(error: unknown): ProjectActionError {
  if (error instanceof ProjectActionError) {
    return error;
  }

  const message =
    typeof error === "object" && error && "message" in error
      ? String(error.message)
      : "Project action failed.";

  const upgradePrompt = parseProjectLimitError(message);
  if (upgradePrompt) {
    return new ProjectActionError(
      "project-limit-reached",
      "Project limit reached. Delete a project or upgrade to keep creating.",
      {
        cause: error,
        upgradePrompt,
      },
    );
  }

  const postgrestError = error as PostgrestError | undefined;
  if (postgrestError?.code === "PGRST116") {
    return new ProjectActionError("not-found", "That project could not be found.", {
      cause: error,
    });
  }

  return new ProjectActionError("generic", message, { cause: error });
}

export function formatProjectErrorMessage(error: unknown) {
  const projectError = mapProjectError(error);

  switch (projectError.code) {
    case "missing-config":
      return "Supabase environment variables are not configured.";
    case "project-limit-reached":
      return "Project limit reached. Delete a project or upgrade to keep creating.";
    case "access-denied":
      return "You do not have access to that project.";
    case "not-found":
      return "That project is unavailable.";
    default:
      return projectError.message || "We could not complete that project action.";
  }
}

async function getProjectCoverUrl(coverImagePath: string | null) {
  if (!coverImagePath) {
    return null;
  }

  try {
    return await createProjectCoverSignedUrl(coverImagePath);
  } catch {
    return null;
  }
}

async function mapProjectWithCover(project: ProjectRecord) {
  const coverImageUrl = await getProjectCoverUrl(project.cover_image_path);
  return mapProjectRecordToCard(project, coverImageUrl);
}

function getProjectArtstylePaths(project: ProjectRecord) {
  return Array.isArray(project.artstyle_ref_paths)
    ? project.artstyle_ref_paths.filter(
        (path): path is string => typeof path === "string" && path.trim().length > 0,
      )
    : [];
}

async function createProjectArtstyleReferences(paths: string[]) {
  return Promise.all(
    paths.map(async (path) =>
      mapProjectArtstyleReference({
        path,
        previewUrl: await createProjectArtstyleReferenceSignedUrl(path).catch(() => null),
      })),
  );
}

async function mapProjectToImageSettings(
  project: ProjectRecord,
): Promise<ProjectImageSettingsView> {
  const artstyleRefPaths = getProjectArtstylePaths(project);

  return {
    artstyleRefPaths,
    artstyleReferences: await createProjectArtstyleReferences(artstyleRefPaths),
    hasArtDirection: hasProjectArtDirection(project),
    maxReferences: PROJECT_ARTSTYLE_REFERENCE_LIMIT,
    projectId: project.id,
    styleDescription: project.style_description ?? "",
  };
}

function ensureProjectArtstyleLimit(paths: string[]) {
  if (paths.length >= PROJECT_ARTSTYLE_REFERENCE_LIMIT) {
    throw new ProjectActionError(
      "generic",
      "Project art direction supports up to 5 artstyle references.",
    );
  }
}

function validateOrderedArtstylePaths(existingPaths: string[], orderedPaths: string[]) {
  if (existingPaths.length !== orderedPaths.length) {
    throw new ProjectActionError("generic", "Artstyle reference order is invalid.");
  }

  const existingSet = new Set(existingPaths);
  const orderedSet = new Set(orderedPaths);

  if (existingSet.size !== orderedSet.size) {
    throw new ProjectActionError("generic", "Artstyle reference order is invalid.");
  }

  for (const path of orderedSet) {
    if (!existingSet.has(path)) {
      throw new ProjectActionError("generic", "Artstyle reference order is invalid.");
    }
  }
}

async function getOwnedProjectById(projectId: string) {
  ensureConfigured();

  const { data, error } = await supabase
    .from("projects")
    .select("*")
    .eq("id", projectId)
    .maybeSingle();

  if (error) {
    throw mapProjectError(error);
  }

  return (data as ProjectRecord | null) ?? null;
}

async function getProjectImportReview(
  projectId: string,
): Promise<ProjectImportReviewPanelView | null> {
  const importJobResult = await supabase
    .from("import_jobs")
    .select("completed_at, id, payload_path, source_file_name")
    .eq("project_id", projectId)
    .in("status", ["complete", "review_ready", "completed"])
    .not("payload_path", "is", null)
    .order("completed_at", { ascending: false })
    .order("created_at", { ascending: false })
    .limit(1)
    .maybeSingle();

  if (importJobResult.error) {
    throw mapProjectError(importJobResult.error);
  }

  if (!importJobResult.data) {
    return null;
  }

  try {
    const [bundle, summary] = await Promise.all([
      readStoryImportReviewBundle(importJobResult.data.id),
      readStoryImportSummary(importJobResult.data.id).catch(() => null),
    ]);
    const pendingCount = (bundle as { totals?: { pending?: number } }).totals?.pending;

    const hasPendingItems =
      typeof pendingCount === "number"
        ? pendingCount > 0
        : bundle.elements.some((element) => element.reviewStatus === "pending");

    if (!hasPendingItems) {
      return null;
    }

    return mapStoryImportBundleToProjectImportReviewPanel({
      bundle,
      completedAt: summary?.completedAt ?? importJobResult.data.completed_at,
      sourceFileName: summary?.sourceFileName ?? importJobResult.data.source_file_name,
    });
  } catch {
    return null;
  }
}

async function getProjectImportReviewLock(
  projectId: string,
): Promise<ProjectImportReviewLockView | null> {
  const importJobResult = await supabase
    .from("import_jobs")
    .select("completed_at, id, payload_path, source_file_name, status")
    .eq("project_id", projectId)
    .in("status", ["complete", "review_ready", "completed"])
    .order("completed_at", { ascending: false })
    .order("created_at", { ascending: false })
    .limit(1)
    .maybeSingle();

  if (importJobResult.error) {
    throw mapProjectError(importJobResult.error);
  }

  if (!importJobResult.data?.payload_path) {
    return null;
  }

  try {
    const bundle = await readStoryImportReviewBundle(importJobResult.data.id);
    const pendingCount = (bundle as { totals?: { pending?: number } }).totals?.pending;

    const hasPendingItems =
      typeof pendingCount === "number"
        ? pendingCount > 0
        : bundle.elements.some((element) => element.reviewStatus === "pending");

    if (!hasPendingItems) {
      return null;
    }

    return {
      completedAt: importJobResult.data.completed_at,
      importJobId: importJobResult.data.id,
      projectId,
      requiredResolution: bundle.requiredResolution,
      reviewRoute: buildProjectStoryImportReviewRoute(projectId, importJobResult.data.id),
      sourceFileName: importJobResult.data.source_file_name,
    };
  } catch {
    return null;
  }
}

async function getProjectActiveImportJob(
  projectId: string,
): Promise<ProjectActiveImportJobView | null> {
  const importJobResult = await supabase
    .from("import_jobs")
    .select(
      "current_pass, detected_language, id, pass_label, source_file_name, status, total_passes",
    )
    .eq("project_id", projectId)
    .in("status", ["queued", "pending", "processing", "canceling"])
    .order("created_at", { ascending: false })
    .limit(1)
    .maybeSingle();

  if (importJobResult.error) {
    throw mapProjectError(importJobResult.error);
  }

  if (!importJobResult.data) {
    return null;
  }

  return {
    currentPass: importJobResult.data.current_pass ?? 0,
    detectedLanguage: importJobResult.data.detected_language ?? null,
    importRoute: `${buildProjectStoryImportRoute(projectId)}?jobId=${importJobResult.data.id}`,
    jobId: importJobResult.data.id,
    passLabel: importJobResult.data.pass_label ?? null,
    reviewRoute: buildProjectStoryImportReviewRoute(projectId, importJobResult.data.id),
    sourceFileName: importJobResult.data.source_file_name,
    status: importJobResult.data.status,
    totalPasses: importJobResult.data.total_passes ?? 12,
  };
}

function toJsonContent(value: unknown): JSONContent | null {
  if (value && typeof value === "object" && "type" in (value as Record<string, unknown>)) {
    return value as JSONContent;
  }

  return null;
}

function extractRichTextText(content: JSONContent | null): string {
  if (!content) {
    return "";
  }

  const text: string[] = [];
  const stack: JSONContent[] = [content];

  while (stack.length > 0) {
    const node = stack.pop();
    if (!node) {
      continue;
    }

    if (typeof node.text === "string") {
      text.push(node.text);
    }

    if (Array.isArray(node.content)) {
      for (let index = node.content.length - 1; index >= 0; index -= 1) {
        stack.push(node.content[index] as JSONContent);
      }
    }
  }

  return text.join(" ").replace(/\s+/g, " ").trim();
}

function countWords(content: unknown) {
  const text = extractRichTextText(toJsonContent(content));
  return text ? text.split(/\s+/).length : 0;
}

function getImagePaths(value: unknown) {
  return Array.isArray(value)
    ? value.filter((item): item is string => typeof item === "string" && item.trim().length > 0)
    : [];
}

function resolveActivityAction(
  createdAt: string,
  updatedAt: string,
): ProjectDashboardActivityAction {
  return createdAt === updatedAt ? "created" : "updated";
}

function buildRecentActivity(
  projectId: string,
  input: {
    chapters: DashboardChapterRow[];
    entities: DashboardEntityRow[];
    loreEntries: DashboardLoreEntryRow[];
    stories: DashboardStoryRow[];
    timelineEvents: DashboardTimelineRow[];
  },
): ProjectDashboardActivityItem[] {
  const storyActivities = input.stories.map((story) => ({
    action: resolveActivityAction(story.created_at, story.updated_at),
    href: `/projects/${projectId}/stories`,
    id: story.id,
    kind: "story" as const,
    occurredAt: story.updated_at,
    title: story.title,
  }));

  const chapterActivities = input.chapters.map((chapter) => ({
    action: resolveActivityAction(chapter.created_at, chapter.updated_at),
    href: `/projects/${projectId}/stories?chapterId=${chapter.id}`,
    id: chapter.id,
    kind: "chapter" as const,
    occurredAt: chapter.updated_at,
    title: chapter.title,
  }));

  const loreActivities = input.loreEntries.map((entry) => ({
    action: resolveActivityAction(entry.created_at, entry.updated_at),
    href: `/projects/${projectId}/lore/entries/${entry.id}`,
    id: entry.id,
    kind: "lore_entry" as const,
    occurredAt: entry.updated_at,
    title: entry.title,
  }));

  const entityActivities = input.entities.map((entity) => ({
    action: resolveActivityAction(entity.created_at, entity.updated_at),
    href: getEntityCardHref(projectId, entity.id),
    id: entity.id,
    kind: "entity_card" as const,
    occurredAt: entity.updated_at,
    title: entity.name,
  }));

  const timelineActivities = input.timelineEvents.map((event) => ({
    action: resolveActivityAction(event.created_at, event.updated_at),
    href: `/projects/${projectId}/timeline`,
    id: event.id,
    kind: "timeline_event" as const,
    occurredAt: event.updated_at,
    title: event.title,
  }));

  return [
    ...storyActivities,
    ...chapterActivities,
    ...loreActivities,
    ...entityActivities,
    ...timelineActivities,
  ]
    .sort(
      (left, right) =>
        new Date(right.occurredAt).getTime() - new Date(left.occurredAt).getTime(),
    )
    .slice(0, 5);
}

function countProjectWords(
  stories: DashboardStoryRow[],
  chapters: DashboardChapterRow[],
) {
  const storyWordCount = stories.reduce((total, story) => {
    if (story.type === "main" && story.mode === "chapters") {
      return total;
    }

    return total + countWords(story.body);
  }, 0);

  const chapterWordCount = chapters.reduce(
    (total, chapter) => total + countWords(chapter.body),
    0,
  );

  return storyWordCount + chapterWordCount;
}

function countProjectImages(input: {
  chapters: DashboardChapterRow[];
  entities: DashboardEntityRow[];
  loreEntries: DashboardLoreEntryRow[];
  stories: DashboardStoryRow[];
  timelineEvents: DashboardTimelineRow[];
}) {
  const storyImageCount = input.stories.reduce(
    (total, story) => total + getImagePaths(story.image_paths).length,
    0,
  );

  return storyImageCount
    + input.chapters.filter((chapter) => Boolean(chapter.image_path)).length
    + input.entities.filter((entity) => Boolean(entity.image_path)).length
    + input.loreEntries.filter((entry) => Boolean(entry.cover_image_path)).length
    + input.timelineEvents.filter((event) => Boolean(event.image_path)).length;
}

export async function getCurrentProjectCapacity(): Promise<ProjectCapacitySummary> {
  ensureConfigured();

  const { data, error } = await supabase.rpc("get_current_project_capacity");

  if (error) {
    throw mapProjectError(error);
  }

  const capacityRow = data?.[0];

  if (!capacityRow) {
    return {
      tier: "free",
      projectsUsed: 0,
      projectsLimit: PROJECT_LIMITS.free,
      projectsRemaining: PROJECT_LIMITS.free,
    };
  }

  return mapCapacityRow({
    projects_limit: capacityRow.projects_limit,
    projects_remaining: capacityRow.projects_remaining,
    projects_used: capacityRow.projects_used,
    tier: capacityRow.tier,
  });
}

export async function getProjectAccessSelectionState(): Promise<ProjectAccessSelectionState> {
  ensureConfigured();

  const { data, error } = await supabase.rpc("get_project_access_selection_state");

  if (error) {
    throw mapProjectError(error);
  }

  return mapProjectAccessSelectionState(data);
}

export async function submitProjectAccessSelection(
  selectedProjectIds: string[],
): Promise<void> {
  ensureConfigured();

  const { error } = await supabase.rpc("submit_project_access_selection", {
    selected_project_ids: selectedProjectIds,
  });

  if (error) {
    throw mapProjectError(error);
  }
}

export async function listProjects(): Promise<ProjectCardView[]> {
  ensureConfigured();

  const { data, error } = await supabase
    .from("projects")
    .select("*")
    .order("updated_at", { ascending: false });

  if (error) {
    throw mapProjectError(error);
  }

  const projects = await Promise.all(
    ((data as ProjectRecord[] | null) ?? []).map((project) => mapProjectWithCover(project)),
  );

  return sortProjectsByLastModified(projects);
}

export async function createProject(input: CreateProjectInput): Promise<ProjectCardView> {
  ensureConfigured();
  const validated = projectSchema.parse(input);
  const userId = await getCurrentUserId();

  const { data, error } = await supabase
    .from("projects")
    .insert({
      user_id: userId,
      title: validated.title,
      genre: validated.genre,
      status: "active" satisfies ProjectStatus,
    })
    .select("*")
    .single();

  if (error) {
    throw mapProjectError(error);
  }

  let project = data as ProjectRecord;

  if (validated.coverImageFile) {
    try {
      const coverImagePath = await uploadProjectCover(
        userId,
        project.id,
        validated.coverImageFile,
      );

      const updateResult = await supabase
        .from("projects")
        .update({
          cover_image_path: coverImagePath,
        })
        .eq("id", project.id)
        .select("*")
        .single();

      if (updateResult.error) {
        throw updateResult.error;
      }

      project = updateResult.data as ProjectRecord;
    } catch (error) {
      await supabase.from("projects").delete().eq("id", project.id);
      throw mapProjectError(error);
    }
  }

  return mapProjectWithCover(project);
}

export async function updateProject(
  projectId: string,
  input: UpdateProjectInput,
): Promise<ProjectCardView> {
  ensureConfigured();
  const validated = updateProjectSchema.parse(input);
  const userId = await getCurrentUserId();
  const existingProject = await getOwnedProjectById(projectId);

  if (!existingProject) {
    throw new ProjectActionError("not-found", "That project is unavailable.");
  }

  let coverImagePath = existingProject.cover_image_path;

  if (validated.coverImageFile) {
    coverImagePath = await uploadProjectCover(userId, projectId, validated.coverImageFile);
  } else if (validated.removeCoverImage && existingProject.cover_image_path) {
    await deleteProjectCover(existingProject.cover_image_path);
    coverImagePath = null;
  }

  const { data, error } = await supabase
    .from("projects")
    .update({
      artstyle_ref_paths: validated.artstyleRefPaths ?? existingProject.artstyle_ref_paths ?? [],
      title: validated.title,
      genre: validated.genre,
      cover_image_path: coverImagePath,
      style_description: validated.styleDescription ?? existingProject.style_description ?? null,
    })
    .eq("id", projectId)
    .select("*")
    .single();

  if (error) {
    throw mapProjectError(error);
  }

  return mapProjectWithCover(data as ProjectRecord);
}

export async function getProjectImageSettings(
  projectId: string,
): Promise<ProjectImageSettingsView> {
  const project = await getOwnedProjectById(projectId);

  if (!project) {
    throw new ProjectActionError("not-found", "That project is unavailable.");
  }

  return mapProjectToImageSettings(project);
}

export async function updateProjectArtDirection(
  projectId: string,
  input: ProjectArtDirectionUpdateInput,
): Promise<ProjectImageSettingsView> {
  ensureConfigured();
  const validated = projectArtDirectionSchema.parse(input);
  const existingProject = await getOwnedProjectById(projectId);

  if (!existingProject) {
    throw new ProjectActionError("not-found", "That project is unavailable.");
  }

  const { data, error } = await supabase
    .from("projects")
    .update({
      style_description: validated.styleDescription || null,
    })
    .eq("id", projectId)
    .select("*")
    .single();

  if (error) {
    throw mapProjectError(error);
  }

  return mapProjectToImageSettings(data as ProjectRecord);
}

export async function addProjectArtstyleReference(
  projectId: string,
  file: File,
): Promise<ProjectImageSettingsView> {
  ensureConfigured();
  const userId = await getCurrentUserId();
  const existingProject = await getOwnedProjectById(projectId);

  if (!existingProject) {
    throw new ProjectActionError("not-found", "That project is unavailable.");
  }

  const existingPaths = getProjectArtstylePaths(existingProject);
  ensureProjectArtstyleLimit(existingPaths);

  const newPath = await uploadProjectArtstyleReference(userId, projectId, file);

  try {
    const { data, error } = await supabase
      .from("projects")
      .update({
        artstyle_ref_paths: [...existingPaths, newPath],
      })
      .eq("id", projectId)
      .select("*")
      .single();

    if (error) {
      throw error;
    }

    return mapProjectToImageSettings(data as ProjectRecord);
  } catch (error) {
    await deleteProjectArtstyleReference(newPath).catch(() => undefined);
    throw mapProjectError(error);
  }
}

export async function removeProjectArtstyleReference(
  projectId: string,
  referencePath: string,
): Promise<ProjectImageSettingsView> {
  ensureConfigured();
  const existingProject = await getOwnedProjectById(projectId);

  if (!existingProject) {
    throw new ProjectActionError("not-found", "That project is unavailable.");
  }

  const existingPaths = getProjectArtstylePaths(existingProject);
  const nextPaths = existingPaths.filter((path) => path !== referencePath);

  const { data, error } = await supabase
    .from("projects")
    .update({
      artstyle_ref_paths: nextPaths,
    })
    .eq("id", projectId)
    .select("*")
    .single();

  if (error) {
    throw mapProjectError(error);
  }

  if (existingPaths.includes(referencePath)) {
    await deleteProjectArtstyleReference(referencePath).catch(() => undefined);
  }

  return mapProjectToImageSettings(data as ProjectRecord);
}

export async function replaceProjectArtstyleReference(
  projectId: string,
  referencePath: string,
  file: File,
): Promise<ProjectImageSettingsView> {
  ensureConfigured();
  const userId = await getCurrentUserId();
  const existingProject = await getOwnedProjectById(projectId);

  if (!existingProject) {
    throw new ProjectActionError("not-found", "That project is unavailable.");
  }

  const existingPaths = getProjectArtstylePaths(existingProject);
  const targetIndex = existingPaths.indexOf(referencePath);

  if (targetIndex < 0) {
    throw new ProjectActionError("not-found", "That artstyle reference is unavailable.");
  }

  const newPath = await uploadProjectArtstyleReference(userId, projectId, file);

  try {
    const nextPaths = [...existingPaths];
    nextPaths[targetIndex] = newPath;

    const { data, error } = await supabase
      .from("projects")
      .update({
        artstyle_ref_paths: nextPaths,
      })
      .eq("id", projectId)
      .select("*")
      .single();

    if (error) {
      throw error;
    }

    await deleteProjectArtstyleReference(referencePath).catch(() => undefined);
    return mapProjectToImageSettings(data as ProjectRecord);
  } catch (error) {
    await deleteProjectArtstyleReference(newPath).catch(() => undefined);
    throw mapProjectError(error);
  }
}

export async function reorderProjectArtstyleReferences(
  projectId: string,
  orderedPaths: string[],
): Promise<ProjectImageSettingsView> {
  ensureConfigured();
  const existingProject = await getOwnedProjectById(projectId);

  if (!existingProject) {
    throw new ProjectActionError("not-found", "That project is unavailable.");
  }

  const existingPaths = getProjectArtstylePaths(existingProject);
  validateOrderedArtstylePaths(existingPaths, orderedPaths);

  const { data, error } = await supabase
    .from("projects")
    .update({
      artstyle_ref_paths: orderedPaths,
    })
    .eq("id", projectId)
    .select("*")
    .single();

  if (error) {
    throw mapProjectError(error);
  }

  return mapProjectToImageSettings(data as ProjectRecord);
}

export async function archiveProject(projectId: string): Promise<ProjectCardView> {
  ensureConfigured();

  const { data, error } = await supabase
    .from("projects")
    .update({
      status: "archived",
    })
    .eq("id", projectId)
    .select("*")
    .single();

  if (error) {
    throw mapProjectError(error);
  }

  return mapProjectWithCover(data as ProjectRecord);
}

export async function deleteProject(projectId: string) {
  ensureConfigured();
  const existingProject = await getOwnedProjectById(projectId);

  if (!existingProject) {
    throw new ProjectActionError("not-found", "That project is unavailable.");
  }

  const { error } = await supabase
    .from("projects")
    .delete()
    .eq("id", projectId);

  if (error) {
    throw mapProjectError(error);
  }

  if (existingProject.cover_image_path) {
    await deleteProjectCover(existingProject.cover_image_path).catch(() => undefined);
  }

  return {
    deletedProjectId: projectId,
  };
}

export async function getProjectDashboardSummary(
  projectId: string,
): Promise<ProjectDashboardSummary | null> {
  const [
    project,
    capacity,
    storiesResult,
    loreResult,
    entityResult,
    timelineResult,
    importReview,
    importReviewLock,
    activeImportJob,
  ] = await Promise.all([
    getOwnedProjectById(projectId),
    getCurrentProjectCapacity(),
    supabase
      .from("stories")
      .select("id, project_id, title, body, image_paths, created_at, updated_at, type, mode")
      .eq("project_id", projectId),
    supabase
      .from("lore_entries")
      .select("id, project_id, title, cover_image_path, created_at, updated_at")
      .eq("project_id", projectId),
    supabase
      .from("entity_cards")
      .select("id, project_id, name, image_path, created_at, updated_at")
      .eq("project_id", projectId),
    supabase
      .from("timeline_events")
      .select("id, project_id, title, image_path, created_at, updated_at")
      .eq("project_id", projectId),
    getProjectImportReview(projectId),
    getProjectImportReviewLock(projectId),
    getProjectActiveImportJob(projectId),
  ]);

  if (!project) {
    return null;
  }

  if (storiesResult.error) {
    throw mapProjectError(storiesResult.error);
  }

  if (loreResult.error) {
    throw mapProjectError(loreResult.error);
  }

  if (entityResult.error) {
    throw mapProjectError(entityResult.error);
  }

  if (timelineResult.error) {
    throw mapProjectError(timelineResult.error);
  }

  const stories = (storiesResult.data as DashboardStoryRow[] | null) ?? [];
  const storyIds = stories.map((story) => story.id);
  const chaptersResult = storyIds.length
    ? await supabase
        .from("chapters")
        .select("id, story_id, title, body, image_path, created_at, updated_at")
        .in("story_id", storyIds)
    : { data: [] as DashboardChapterRow[], error: null };

  if (chaptersResult.error) {
    throw mapProjectError(chaptersResult.error);
  }

  const chapters = (chaptersResult.data as DashboardChapterRow[] | null) ?? [];
  const loreEntries = (loreResult.data as DashboardLoreEntryRow[] | null) ?? [];
  const entities = (entityResult.data as DashboardEntityRow[] | null) ?? [];
  const timelineEvents = (timelineResult.data as DashboardTimelineRow[] | null) ?? [];
  const wordCount = countProjectWords(stories, chapters);
  const imageCount = countProjectImages({
    chapters,
    entities,
    loreEntries,
    stories,
    timelineEvents,
  });
  const recentActivity = buildRecentActivity(projectId, {
    chapters,
    entities,
    loreEntries,
    stories,
    timelineEvents,
  });

  const card = await mapProjectWithCover(project);
  return buildProjectDashboardSummary(card, capacity, {
    activeImportJob,
    entityCardCount: entities.length,
    imageCount,
    importReview,
    importReviewLock,
    loreEntryCount: loreEntries.length,
    recentActivity,
    timelineEventCount: timelineEvents.length,
    wordCount,
  });
}
